import React, { useCallback, useState } from 'react'
import { Card, Table, Button, Form, DatePicker, Input, Row, Col, Tooltip } from 'antd';
import { CloudDownloadOutlined, SearchOutlined, FilterOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import apiClient from 'services/apiClient';
import { useQuery } from '@tanstack/react-query';
import { useMyContext } from 'Context/MyContextProvider';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import { UserSelectField } from '../CustomUtils.js/CommonFieldUtils';
import { GetParams } from '../Utils/utils';
import CustomDrawer from 'components/shared-components/CustomDrawer';

const { RangePicker } = DatePicker;

const ReportsFilters = ({
    isMobile,
    userId,
    onUserChange,
    dateRange,
    onDateRangeChange,
    userRole
}) => {
    if (isMobile) {
        return (
            <Form layout="vertical">
                {(userRole === 'Admin' || userRole === 'Reseller') && (
                    <Form.Item label="User" className="mb-4">
                        <UserSelectField value={userId} HandleUserChange={onUserChange} />
                    </Form.Item>
                )}
                <Form.Item label="Date Range" className="mb-4">
                    <RangePicker
                        placeholder={['Start Date', 'End Date']}
                        style={{ width: '100%' }}
                        value={dateRange}
                        allowClear
                        onChange={onDateRangeChange}
                    />
                </Form.Item>
            </Form>
        );
    }

    return (
        <>
            {(userRole === 'Admin' || userRole === 'Reseller') && (
                <Col span={24} lg={12} xl={6}>
                    <div className="mb-3">
                        <Form.Item className='mb-0'>
                            <UserSelectField value={userId} HandleUserChange={onUserChange} />
                        </Form.Item>
                    </div>
                </Col>
            )}
            <Col span={24} lg={12} xl={6}>
                <div className="mb-3">
                    <Form.Item className='mb-0'>
                        <RangePicker
                            placeholder={['Start Date', 'End Date']}
                            style={{ width: '100%' }}
                            allowClear 
                            onChange={onDateRangeChange}
                        />
                    </Form.Item>
                </div>
            </Col>
        </>
    );
};

const Reports = () => {
    const { UserData, Permisson, DownloadExcelFile, encryptData, userRole, isMobile } = useMyContext();

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [searchValue, setSearchValue] = useState('');
    const [userId, setUserId] = useState(UserData?.id);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [draftUserId, setDraftUserId] = useState(userId);
    const [draftStartDate, setDraftStartDate] = useState(startDate);
    const [draftEndDate, setDraftEndDate] = useState(endDate);
    const [draftDateRange, setDraftDateRange] = useState(null);

    const isFilterApplied = userId !== UserData?.id || !!startDate || !!endDate;

    const handleDraftDateRangeChange = (dates) => {
        setDraftDateRange(dates);
        if (dates && dates.length > 0) {
            const sortedDates = dates.slice().sort((a, b) => new Date(a) - new Date(b));
            const formattedDates = sortedDates.map(date => dayjs(date).format('DD/MM/YY'));
            setDraftStartDate(formattedDates[0]);
            setDraftEndDate(formattedDates[formattedDates.length - 1]);
        } else {
            setDraftStartDate(null);
            setDraftEndDate(null);
        }
    };

    const openFilterDrawer = () => {
        setDraftUserId(userId);
        setDraftStartDate(startDate);
        setDraftEndDate(endDate);
        if (startDate && endDate) {
            setDraftDateRange([
                dayjs(startDate, 'DD/MM/YY'),
                dayjs(endDate, 'DD/MM/YY')
            ]);
        } else {
            setDraftDateRange(null);
        }
        setDrawerVisible(true);
    };

    const handleApplyFilters = () => {
        setUserId(draftUserId);
        setStartDate(draftStartDate);
        setEndDate(draftEndDate);
        resetToFirstPage();
        setDrawerVisible(false);
    };

    const handleClearFilters = () => {
        setUserId(UserData?.id);
        setStartDate(null);
        setEndDate(null);
        resetToFirstPage();
        setDrawerVisible(false);
    };

    const tableColumns = [
        {
            title: 'Name',
            dataIndex: 'Name',
        },
        {
            title: 'Message',
            dataIndex: 'Message',
            render: Message => {
                const containsNumbers = Message?.match(/\d+/g);
                if (containsNumbers && Permisson?.includes('Encrypt content')) {
                    let encryptedText = Message;
                    containsNumbers.forEach(numericValue => {
                        encryptedText = encryptedText?.replace(numericValue, encryptData(numericValue));
                    });
                    return encryptedText;
                }
                return Message;
            },
        },
        {
            title: 'From',
            dataIndex: 'From',
            render: From => {
                const data = Permisson?.includes('Encrypt Number') ? encryptData(From) : From;
                return <span>{data}</span>;
            },
        },
        {
            title: 'To',
            dataIndex: 'To',
        },
        {
            title: 'Date',
            dataIndex: 'Date',
            render: Date => {
                const date = dayjs.unix(Date);
                const formattedDate = date.format('MM/DD/YYYY');
                const formattedTime = date.format('hh:mm:ss A');
                return (
                    <span>
                        {formattedDate} | {formattedTime}
                    </span>
                );
            },
        },
    ];

    const { current, pageSize } = pagination;

    const fetchReportsQuery = async () => {
        const params = {
            ...GetParams(startDate, endDate, searchValue),
            page: current,
            per_page: pageSize,
        };
        const response = await apiClient.get(`/reports/${userId}`, { params });
        return response.data?.data ?? response.data;
    };

    const { data, isLoading: loading, refetch, isFetching } = useQuery({
        queryKey: ['reports', userId, startDate, endDate, searchValue, current, pageSize],
        queryFn: fetchReportsQuery,
        enabled: !!userId,
        keepPreviousData: true,
    });

    const reports = data?.reports || [];
    const total = data?.pagination?.total || 0;

    const resetToFirstPage = () => {
        setPagination((prev) => ({ ...prev, current: 1 }));
    };

    const HandleUserChange = (value) => {
        setUserId(value);
        resetToFirstPage();
    };

    const HandleDateRangeChange = (dates) => {
        if (dates && dates.length > 0) {
            const sortedDates = dates.slice().sort((a, b) => new Date(a) - new Date(b));
            const formattedDates = sortedDates.map(date => dayjs(date).format('DD/MM/YY'));
            setStartDate(formattedDates[0]);
            setEndDate(formattedDates[formattedDates.length - 1]);
        } else {
            setStartDate(null);
            setEndDate(null);
        }
        resetToFirstPage();
    };

    const onSearch = (e) => {
        if (e.key === 'Enter') {
            resetToFirstPage();
        }
    };

    const handleTableChange = (tablePagination) => {
        setPagination((prev) => ({
            ...prev,
            current: tablePagination.current,
            pageSize: tablePagination.pageSize,
        }));
    };

    const handleExportWithEncryption = useCallback(() => {
        const encryptedReports = reports.map(item => ({
            ...item,
            Message: item.Message
                ? (Permisson?.includes('Encrypt content')
                    ? item.Message.replace(/\d+/g, match => encryptData(match))
                    : item.Message)
                : '',
            From: item.From
                ? (Permisson?.includes('Encrypt Number') ? encryptData(item.From) : item.From)
                : '',
        }));
        DownloadExcelFile(encryptedReports, 'incoming-report.xlsx');
    }, [reports, Permisson, encryptData, DownloadExcelFile]);

    return (
        <Card bodyStyle={{ padding: '0px' }}>
            <div className="container-fluid">
                <Form
                    name="basicInformation"
                    layout={`${isMobile ? 'vertical' : 'horizontal'} `}
                >
                    <Row gutter={ROW_GUTTER} justify="space-between" align="middle" className="py-4">
                        <Col xs={24} sm={24} lg={6} xl={4}>
                            <h2>Incoming Reports</h2>
                        </Col>
                        <Col xs={24} sm={24} lg={18} xl={20}>
                            {isMobile ? (
                                <Row gutter={[8, 8]} justify="end" align="middle">
                                    <Col xs={4}>
                                        <div className="mb-3">
                                            <Tooltip title="Refresh">
                                                <Button
                                                    block
                                                    icon={<ReloadOutlined />}
                                                    onClick={() => refetch()}
                                                    loading={isFetching}
                                                />
                                            </Tooltip>
                                        </div>
                                    </Col>
                                    <Col xs={10}>
                                        <div className="mb-3">
                                            <Form.Item className='mb-0'>
                                                <Input placeholder="Search" prefix={<SearchOutlined />}
                                                    onChange={e => {
                                                        setSearchValue(e.currentTarget.value.toLowerCase());
                                                        resetToFirstPage();
                                                    }}
                                                    onKeyDown={(e) => onSearch(e)}
                                                />
                                            </Form.Item>
                                        </div>
                                    </Col>
                                    <Col xs={5}>
                                        <div className="mb-3">
                                            <Button 
                                                block
                                                type={isFilterApplied ? 'primary' : 'default'}
                                                icon={<FilterOutlined />} 
                                                onClick={openFilterDrawer}
                                            />
                                        </div>
                                    </Col>
                                    <Col xs={5}>
                                        <div className="mb-3">
                                            <Button 
                                                block
                                                icon={<CloudDownloadOutlined />} 
                                                onClick={handleExportWithEncryption}
                                            />
                                        </div>
                                    </Col>
                                </Row>
                            ) : (
                                <Row gutter={ROW_GUTTER} justify="end" align="middle">
                                    <Col span={2} lg={2} xl={1}>
                                        <div className="mb-3">
                                            <Tooltip title="Refresh">
                                                <Button
                                                    block
                                                    icon={<ReloadOutlined />}
                                                    onClick={() => refetch()}
                                                    loading={isFetching}
                                                />
                                            </Tooltip>
                                        </div>
                                    </Col>
                                    <ReportsFilters
                                        isMobile={false}
                                        userId={userId}
                                        onUserChange={HandleUserChange}
                                        onDateRangeChange={HandleDateRangeChange}
                                        userRole={userRole}
                                    />
                                    <Col span={12} lg={10} xl={5}>
                                        <div className="mb-3">
                                            <Form.Item className='mb-0'>
                                                <Input placeholder="Search" prefix={<SearchOutlined />}
                                                    onChange={e => {
                                                        setSearchValue(e.currentTarget.value.toLowerCase());
                                                        resetToFirstPage();
                                                    }}
                                                    onKeyDown={(e) => onSearch(e)}
                                                />
                                            </Form.Item>
                                        </div>
                                    </Col>
                                    <Col span={10} lg={12} xl={6}>
                                        <div className="mb-3">
                                            <Button
                                                block
                                                icon={<CloudDownloadOutlined />}
                                                onClick={handleExportWithEncryption}>
                                                Export Report</Button>
                                        </div>
                                    </Col>
                                </Row>
                            )}
                        </Col>
                    </Row>
                </Form>
            </div>

            <div className="table-responsive">
                <Table
                    loading={loading}
                    columns={tableColumns}
                    dataSource={reports}
                    rowKey="id"
                    pagination={{
                        current: current,
                        pageSize: pageSize,
                        total: total,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50'],
                        showTotal: (total) => `Total ${total} reports`,
                    }}
                    onChange={handleTableChange}
                />
            </div>

            <CustomDrawer
                title="Filter Reports"
                open={drawerVisible}
                onClose={() => setDrawerVisible(false)}
                footer={
                    <div style={{ display: 'flex', gap: '8px' }}>
                        {isFilterApplied && (
                            <Button onClick={handleClearFilters} style={{ flex: 1 }}>
                                Clear
                            </Button>
                        )}
                        <Button type="primary" onClick={handleApplyFilters} style={{ flex: 1 }}>
                            Apply
                        </Button>
                    </div>
                }
            >
                <ReportsFilters
                    isMobile={true}
                    userId={draftUserId}
                    onUserChange={setDraftUserId}
                    dateRange={draftDateRange}
                    onDateRangeChange={handleDraftDateRangeChange}
                    userRole={userRole}
                />
            </CustomDrawer>
        </Card >
    );
};

export default Reports
